import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { Sparkle, EnvelopeSimple, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * NudgeComposerDialog — Iter 85.6 · Follow-up Nudge Composer.
 *
 * Partner clicks a "Reach out" chip on a decayed contact → we call
 * /contacts/{cid}/{contact_id}/nudge-draft, Claude drafts a warm,
 * specific opener grounded in the contact's cadence + most recent
 * note on the client. Partner reviews, edits inline, copies to
 * clipboard or fires their mail client via mailto:.
 *
 * No real send happens here — this is a compose surface, not a delivery
 * channel. Delivery lives with Outlook/Resend which are already wired.
 */
export default function NudgeComposerDialog({ client, contact, open, onOpenChange }) {
  const [channel, setChannel] = useState("email");
  const [tone, setTone] = useState("warm");
  const [subject, setSubject] = useState("");
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!client?.id || !contact?.id) return;
    setLoading(true);
    try {
      const { data } = await api.post(
        `/contacts/${client.id}/${contact.id}/nudge-draft`,
        { channel, tone },
      );
      setDraft(data.draft || "");
      setSubject(data.subject || "");
      // Iter 85.7 · Nudge Send History — log the "opened" action the
      // first time a draft renders. Fades the "Reach out" chip for 14d
      // so a partner who acted isn't nagged repeatedly.
      try { await api.post(`/contacts/${client.id}/${contact.id}/nudge-log`, { action: "opened", channel }); } catch { /* silent */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && client?.id && contact?.id) {
      setDraft(""); setSubject("");
      generate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact?.id, channel, tone]);

  const copyText = async () => {
    const payload = channel === "email" && subject ? `Subject: ${subject}\n\n${draft}` : draft;
    try {
      await navigator.clipboard.writeText(payload);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const mailto = () => {
    if (!contact?.email) return toast.error("Contact has no email on file");
    const url = `mailto:${contact.email}?subject=${encodeURIComponent(subject || "")}&body=${encodeURIComponent(draft || "")}`;
    window.open(url, "_blank");
    // Iter 85.7 · Log the "sent" action — fades the "Reach out" chip for
    // 14 days. Non-blocking; a failure here doesn't stop the mailto.
    if (client?.id && contact?.id) {
      api.post(`/contacts/${client.id}/${contact.id}/nudge-log`, { action: "sent", channel }).catch(() => {});
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-2xl" data-testid="nudge-composer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkle size={14} weight="fill" className="text-primary" />
            Reach out to {contact?.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Claude drafts an opener grounded in this contact's cadence and the most recent note on {client?.name}.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Channel</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="rounded-none bg-secondary/40 mt-1" data-testid="nudge-channel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="linkedin">LinkedIn</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Tone</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger className="rounded-none bg-secondary/40 mt-1" data-testid="nudge-tone"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="warm">Warm</SelectItem>
                <SelectItem value="brief">Brief</SelectItem>
                <SelectItem value="checkin">Check-in</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {channel === "email" && (
          <div>
            <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="rounded-none bg-secondary/40 mt-1"
              data-testid="nudge-subject"
            />
          </div>
        )}

        <div>
          <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
            Draft {loading && <span className="text-primary/70">· generating…</span>}
          </Label>
          <Textarea
            rows={10}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={loading ? "Claude is thinking…" : "Draft will appear here"}
            className="rounded-none bg-secondary/40 mt-1 font-serif text-sm leading-relaxed"
            data-testid="nudge-draft"
          />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-none" onClick={generate} disabled={loading} data-testid="nudge-regenerate">
            Regenerate
          </Button>
          <Button variant="outline" className="rounded-none" onClick={copyText} disabled={!draft} data-testid="nudge-copy">
            <Copy size={12} className="mr-1" /> Copy
          </Button>
          <Button
            onClick={mailto}
            disabled={!draft || !contact?.email}
            className="rounded-none bg-primary text-primary-foreground"
            data-testid="nudge-mailto"
          >
            <EnvelopeSimple size={12} className="mr-1" /> Open email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
